import {
  API_BASE_URL,
  API_PROXY_DISABLE_REWRITE,
  API_PROXY_PREFIX,
  API_TARGET_URL,
  USING_DEV_PROXY,
} from '@/lib/data-source/base-url';
import { getProviderDomain } from '@/lib/provider-context';
import { ProviderSettingsRequestError } from '@/services/provider/settings-request';

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: RequestMethod;
  body?: Record<string, unknown> | undefined;
}

function resolveRequestPath(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (USING_DEV_PROXY && API_PROXY_PREFIX && !API_PROXY_DISABLE_REWRITE) {
    if (normalizedPath.startsWith(API_PROXY_PREFIX)) {
      return normalizedPath;
    }

    const prefixed = `${API_PROXY_PREFIX}${normalizedPath}`;
    return prefixed.replace(`${API_PROXY_PREFIX}//`, `${API_PROXY_PREFIX}/`);
  }

  const base = API_BASE_URL || API_TARGET_URL || '';
  if (!base || base === '/') {
    return normalizedPath;
  }

  return `${base}${normalizedPath}`;
}

export async function requestPublicProviderSettings(
  path: string,
  options: RequestOptions = {},
): Promise<Record<string, unknown>> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (method !== 'GET' && method !== 'DELETE') {
    headers['Content-Type'] = 'application/json';
  }

  const providerDomain = getProviderDomain();
  if (providerDomain) {
    headers['X-Provider-Domain'] = providerDomain;
  }

  const response = await fetch(resolveRequestPath(path), {
    method,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
    mode: 'cors',
    credentials: 'omit',
  });

  let payload: Record<string, unknown> | null = null;
  try {
    payload = (await response.json()) as Record<string, unknown>;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const message = (payload as { message?: string } | null)?.message ?? 'Request failed';
    throw new ProviderSettingsRequestError(message, response.status, payload ?? undefined);
  }

  return payload ?? {};
}
